/**
 * CustomerTable Component
 * Main table component for displaying customers with sortable headers
 * Responsive: Shows table on desktop, card layout on mobile
 */

import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Paper,
  Typography,
  Button,
  Alert,
  TableCell,
  Box,
  useMediaQuery,
  useTheme,
  Skeleton,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { Customer } from '../../../types/customer';
import type { SortState, SortableColumn } from '../types/customerTable.types';
import { CustomerTableRow } from './CustomerTableRow';
import { CustomerMobileCard } from './CustomerMobileCard';
import { CustomerTableSkeleton } from './CustomerTableSkeleton';
import { SortableTableHeader } from './SortableTableHeader';
import { TABLE_COLUMNS } from '../constants/customerTableConfig';

interface CustomerTableProps {
  customers: Customer[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  sortState: SortState;
  onRowClick: (customerId: string) => void;
  onDeleteClick: (event: React.MouseEvent, customer: Customer) => void;
  onAddCustomer: () => void;
  onSort: (columnId: SortableColumn) => void;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  isLoading,
  isError,
  error,
  sortState,
  onRowClick,
  onDeleteClick,
  onAddCustomer,
  onSort,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Render error state
  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error instanceof Error ? error.message : 'Failed to load customers'}
      </Alert>
    );
  }

  // Mobile Card Layout
  if (isMobile) {
    return (
      <Box sx={{ mb: 2 }}>
        {isLoading ? (
          // Mobile loading skeleton
          <>
            {[...Array(5)].map((_, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  mb: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1.5,
                  p: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mr: 1.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </Box>
                  <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="rounded" width={100} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton variant="rounded" width={80} height={32} />
                  <Skeleton variant="rounded" width={80} height={32} />
                </Box>
              </Paper>
            ))}
          </>
        ) : customers.length === 0 ? (
          // Empty state
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: 1.5,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No customers found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Get started by adding your first customer
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddCustomer}
              fullWidth
              sx={{
                backgroundColor: '#3b82f6',
                '&:hover': { backgroundColor: '#2563eb' },
              }}
            >
              New Customer
            </Button>
          </Paper>
        ) : (
          // Customer cards
          customers.map((customer) => (
            <CustomerMobileCard
              key={customer.id}
              customer={customer}
              onRowClick={onRowClick}
              onDeleteClick={onDeleteClick}
            />
          ))
        )}
      </Box>
    );
  }

  // Desktop Table Layout
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: '#e5e7eb',
        borderRadius: 1.5,
      }}
    >
      <Table>
        {/* Table Header with Sortable Columns */}
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f9fafb' }}>
            {TABLE_COLUMNS.map((column) => (
              <SortableTableHeader
                key={column.id}
                column={column}
                sortState={sortState}
                onSort={onSort}
              />
            ))}
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {isLoading ? (
            <CustomerTableSkeleton />
          ) : customers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No customers found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get started by adding your first customer
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={onAddCustomer} sx={{ mt: 2 }}>
                  New Customer
                </Button>
              </TableCell>
            </TableRow>
          ) : (
            customers.map((customer) => (
              <CustomerTableRow
                key={customer.id}
                customer={customer}
                onRowClick={onRowClick}
                onDeleteClick={onDeleteClick}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomerTable;
